/* Copyright (c) 2020-present CloudIO Inc */

import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './SimpleFilter';

const SimpleFilterManager: ComponentManagerFn<'SimpleFilter'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'SimpleFilter'>(platform, 'SimpleFilter'),
    type: 'SimpleFilter',
    name: 'Quick Filter',
    isChildAllowed: (page, item, childType) =>
      (['SimpleFilterAttribute'] as ItemType[]).includes(childType),
    defaultValue,
    defaultChildren: ['SimpleFilterAttribute'],
    props: {
      filterType: {
        label: 'Filter Type',
        options: toOptions(['local', 'remote']),
        type: 'select',
        helpText:
          'Use the Remote option to re-query the store whenever the filter is changed. Otherwise, it will be applied to store data in memory.',
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      showBorder: {
        label: 'Show Border',
        type: 'boolean',
      },
      roundedCorners: {
        label: 'Rounded Corners',
        type: 'boolean',
      },
    },
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'roundedCorners':
          return itemData.showBorder;
        default:
          return true;
      }
    },
    category: 'Data',
    icon: { iconName: 'filter' },
    excludeChildren: true,
    description: `**Overview**<br/>
The Simple Filter (often referred to as Quick Filter) is a store-bound controller designed to provide users with immediate, low-friction filtering capabilities. Unlike complex search forms that require multiple inputs and a "Submit" button, the Simple Filter offers a single input field that dynamically refines the dataset as the user types.
It is best used as a toolbar accessory for Data Grids, Lists, or Kanban Boards, allowing users to quickly narrow down records by Name, ID, or Status without leaving the context of their work.

**1. Filtering Strategy (Local vs. Remote)**<br/>
This is the most critical configuration choice, as it determines how the component interacts with your data source.
* **Filter Type:**
    * **Local:** Filters the data currently loaded in the browser's memory. It does not hit the server. Best for small-to-medium datasets (e.g., < 1,000 records) where all data is pre-fetched. (Note: If the store hasn't been queried yet, this mode will trigger a "blind query" to ensure there is data to filter).
    * **Remote:** Sends a new query to the server every time the filter changes. Essential for large datasets (e.g., 1,000,000+ records) where pagination is used. It ensures the user is searching the entire database, not just the current page.

**2. Defining Filter Logic**<br/>
The Simple Filter acts as a container. To tell it what to filter, you must add child logic components.
* **Child Component (Simple Filter Attribute):** Defines the specific data field (e.g., "EmployeeName", "Email", "Department") that the filter input should target.
* **Composition:** You can add multiple attributes. If you add Name and Email, typing "John" will find records where Name OR Email matches "John".

**3. User Experience**<br/>
* **Placeholder:** The help text shown inside the empty input field. (Best Practice: Use descriptive text like "Search by Name or Email..." so the user knows exactly which fields are being targeted).
* **Styling:**
    * **Show Border:** Toggles the input field's border. Disable this for a cleaner look if the filter is placed inside a toolbar with its own background.
    * **Rounded Corners:** (Visible only if Show Border is enabled) Softens the edges of the input field to match your application's design language.

**4. Integration**<br/>
* **Context Awareness:** This component does not require a Data Source Alias property. Instead, it automatically detects and binds to the nearest Data Store context (e.g., if placed inside a Data Store Reference or a component that owns a store).`,
  };
};

export default SimpleFilterManager;
